import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress, ListItemText, MenuItem } from '@mui/material';
import FixedMenu from 'components/FixedMenu';
import { GridPanelControl } from 'features/layout/grid-panel/GridPanelControl';
import { useSimulcastLayerControl } from 'features/layout/grid-panel/controls/GridPanelSimulcastLayerControl/useSimulcastLayerControl';

type GridPanelSimulcastLayerControlProps = {
  compact?: boolean;
};

export const GridPanelSimulcastLayerControl = ({
  compact = false,
}: GridPanelSimulcastLayerControlProps) => {
  const { t } = useTranslation('room');
  const menuId = useId();

  const {
    controlEnabled,
    layers,
    activeLayer,
    activeLayerLabel,
    isPending,
    selectLayer,
    getLayerLabel,
    tooltip,
    icon,
  } = useSimulcastLayerControl();

  if (!controlEnabled) {
    return null;
  }

  const iconComponent = isPending ? (
    <CircularProgress sx={{ mr: 1 }} size={16} color="inherit" />
  ) : undefined;

  const trigger = compact ? (
    <GridPanelControl icon={icon} iconComponent={iconComponent} tooltip={tooltip} />
  ) : (
    <GridPanelControl
      icon={icon}
      iconComponent={iconComponent}
      tooltip={tooltip}
      text={t('grid.simulcast_quality_label', { quality: activeLayerLabel })}
      sx={{
        width: 'auto',
        paddingX: 3,
        borderRadius: '20px',
        '&:after': { borderRadius: '12px' },
      }}
    />
  );

  return (
    <FixedMenu
      id={menuId}
      trigger={trigger}
      disablePortal
      slotProps={{
        paper: {
          sx: {
            mt: 2,
          },
        },
      }}
    >
      <MenuItem disabled sx={{ minWidth: 180 }}>
        {t('grid.simulcast_layer')}
      </MenuItem>
      {layers.map((index) => (
        <MenuItem key={index} selected={index === activeLayer} onClick={() => selectLayer(index)}>
          <ListItemText>{getLayerLabel(index)}</ListItemText>
        </MenuItem>
      ))}
    </FixedMenu>
  );
};
